import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check, X } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

export default function Decisions() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ title: "", context: "", options: "", recommendation: "", risks: "", deadline: "" });

  const load = async () => setItems((await api.get("/decisions")).data);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/decisions", { ...f, options: f.options.split("\n").filter(Boolean), deadline: f.deadline || null });
    toast.success("Decision queued");
    setShowForm(false);
    setF({ title: "", context: "", options: "", recommendation: "", risks: "", deadline: "" });
    load();
  };

  const setStatus = async (id, status) => {
    await api.patch(`/decisions/${id}`, { status });
    toast.success(status);
    load();
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <PageHeader
        overline="Executive Queue"
        title="Decisions"
        subtitle="Decisions requiring your final authority — with rationale, options, risks."
        actions={<button data-testid="new-decision-btn" onClick={() => setShowForm(true)} className="btn-primary text-sm flex items-center gap-1"><Plus size={14} /> New</button>}
      />

      {showForm && (
        <div className="oz-card p-5 mb-6">
          <form onSubmit={create} className="space-y-3">
            <input required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <textarea placeholder="Context" value={f.context} onChange={(e) => setF({ ...f, context: e.target.value })} rows="3" className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <textarea placeholder="Options (one per line)" value={f.options} onChange={(e) => setF({ ...f, options: e.target.value })} rows="3" className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input placeholder="Recommendation" value={f.recommendation} onChange={(e) => setF({ ...f, recommendation: e.target.value })} className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input placeholder="Risks" value={f.risks} onChange={(e) => setF({ ...f, risks: e.target.value })} className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input type="datetime-local" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm font-mono" />
            <div className="flex gap-2">
              <button type="submit" className="btn-primary text-sm">Save</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {items.length === 0 && <div className="oz-card p-8 text-center text-[color:var(--oz-text-muted)]">No decisions in queue.</div>}
        {items.map((d) => (
          <div key={d.id} className="oz-card p-5" data-testid={`decision-${d.id}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <div className="font-display text-lg tracking-tight mb-1">{d.title}</div>
                <div className="text-sm text-[color:var(--oz-text-dim)]">{d.context}</div>
              </div>
              <span className={`chip ${d.status === "pending" ? "chip-yellow" : d.status === "approved" ? "chip-green" : "chip-red"}`}>{d.status?.toUpperCase()}</span>
            </div>
            {d.options?.length > 0 && (
              <div className="mb-3">
                <div className="overline mb-1">Options</div>
                <ul className="text-sm space-y-1">
                  {d.options.map((o, i) => <li key={i} className="text-[color:var(--oz-text)]">— {o}</li>)}
                </ul>
              </div>
            )}
            {d.recommendation && (
              <div className="mb-3 p-3 border-l-2 border-[color:var(--oz-emerald)] bg-[color:var(--oz-hover)]">
                <div className="overline mb-1">Recommendation</div>
                <div className="text-sm">{d.recommendation}</div>
              </div>
            )}
            {d.risks && <div className="text-xs text-[color:var(--oz-text-muted)] mb-3">Risks: {d.risks}</div>}
            {d.deadline && <div className="text-xs font-mono text-[color:var(--oz-text-muted)] mb-3">Deadline: {new Date(d.deadline).toLocaleString()}</div>}
            {d.status === "pending" && (
              <div className="flex gap-2">
                <button onClick={() => setStatus(d.id, "approved")} data-testid={`approve-${d.id}`} className="btn-primary text-xs flex items-center gap-1"><Check size={12} /> Approve</button>
                <button onClick={() => setStatus(d.id, "rejected")} data-testid={`reject-${d.id}`} className="btn-ghost text-xs flex items-center gap-1"><X size={12} /> Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
